import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { PageHeader } from '@ui-kit';
import { MarkdownView } from '../../dev-standards-viewer';
import { highlightAnchorTarget } from '../../_shared/lib/highlightAnchorTarget';
import { resolveConsumerGuideAsset, type ConsumerGuideAssetLike } from '../lib/resolveConsumerGuideAsset';
import consumerGuideKoJson from '../data/consumer-guide.md.json';
import consumerGuideEnJson from '../data/consumer-guide.en.md.json';
import { findAnchorTarget } from '../../_shared/lib/findAnchorTarget';

/**
 * ConsumerGuidePage — queue #647 §UAT-2026-07-07-R647-3 · queue #660 §R660-4 FAQ 앵커 스크롤 ·
 * queue #686 §UAT-2026-07-14-R686 언어별 렌더 소스 전환 · queue #689 §UAT-2026-07-14-R689
 * §R689-1-⑦ 통합검색 앵커 딥링크 진입 시 섹션 하이라이트.
 *
 * `docs/consumer-guide.md`(+ EN 판 `docs/consumer-guide.en.md`)를 빌드타임 번들
 * (`data/consumer-guide{,.en}.md.json`, prebuild `extract-consumer-guide.mjs`)에서 로드해
 * 인앱 렌더 — 런타임 fetch/외부 GitLab 이탈 0. 마크다운 렌더러는 `dev-standards-viewer` 의
 * `MarkdownView` 를 cross-feature re-export 로 재사용한다(재구현 금지 — §R647-0).
 *
 * #686: `i18n.language` 가 `en` 이고 EN 자산이 유효(`lang !== 'ko-fallback'`)하면 EN 번들을
 * 렌더 + 다운로드 파일명도 `consumer-guide.en.md` 로 전환한다. EN 번역이 아직 없는 상태
 * (`lang === 'ko-fallback'`, extract 스크립트가 KO 를 복제해 채운 산출물)에서는 KO 로 폴백해
 * 빈 화면/깨진 렌더 없이 default 상태를 유지한다. #689: 이 판정은 `resolveConsumerGuideAsset`
 * (SSOT)로 추출해 `useSearchIndex` 의 doc 검색 인덱스와 동일 함수를 공유한다(판정 drift 방지).
 *
 * #660: `RelatedLinkCards` 의 "자주 걸리는 문제" 카드가 `#faq` hash 로 진입하면, 마운트 시
 * `location.hash` 를 읽어 해당 섹션으로 스크롤한다. `rehypeSlug`(MarkdownView)가 부여하는
 * heading id 를 우선 탐색하고, `#faq` 는 id 가 불안정할 수 있어 FAQ 텍스트 매칭 폴백을 둔다
 * (§R660-4 — E9 는 FAQ heading 가시성만 단언, 픽셀 스크롤 위치는 비단언). #689: 스크롤 대상을
 * `highlightAnchorTarget`(`_shared/lib` 순수 유틸)로 1.8s 하이라이트한다(재구현 0).
 *
 * 5상태: 정적 번들이라 default 만 발생(§R647-3) — loading/empty/error/dialog 없음.
 */

const CONSUMER_GUIDE_FILENAME_KO = 'consumer-guide.md';
const CONSUMER_GUIDE_FILENAME_EN = 'consumer-guide.en.md';

interface ConsumerGuideAsset extends ConsumerGuideAssetLike {
  content: string;
  extractedAt: string;
  sourcePath: string;
}

const consumerGuideKo = consumerGuideKoJson as ConsumerGuideAsset;
const consumerGuideEn = consumerGuideEnJson as ConsumerGuideAsset;

/**
 * consumer-guide-content 내부에서 hash id 우선 탐색, 실패 시 FAQ 텍스트 매칭 폴백(§R660-4).
 * 스크롤 대상 엘리먼트를 반환(#689 — 호출자가 `highlightAnchorTarget` 에 넘겨 하이라이트).
 */
function scrollToHashTarget(hash: string): HTMLElement | null {
  if (!hash) return null;
  const id = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!id) return null;

  // #696 — MarkdownView sanitize clobberPrefix(user-content-) 로 heading id 가 바뀌므로 SSOT 조회
  const byId = findAnchorTarget(id);
  if (byId) {
    byId.scrollIntoView({ block: 'start' });
    return byId;
  }

  if (id === 'faq') {
    const container = document.getElementById('consumer-guide-content');
    const headings = container?.querySelectorAll('h1, h2, h3') ?? [];
    for (const heading of Array.from(headings)) {
      if (/faq|자주 걸리는 문제|common issues/i.test(heading.textContent ?? '')) {
        heading.scrollIntoView({ block: 'start' });
        return heading as HTMLElement;
      }
    }
  }
  return null;
}

export function ConsumerGuidePage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const highlightCleanupRef = useRef<(() => void) | null>(null);

  // #686 §R686-1-3 / #689 §R689-1-⑥ — SSOT 판정 함수(useSearchIndex 의 doc 인덱스와 공유).
  const { asset: consumerGuide, useEnGuide } = resolveConsumerGuideAsset(
    i18n.language,
    consumerGuideKo,
    consumerGuideEn
  );
  const downloadFilename = useEnGuide ? CONSUMER_GUIDE_FILENAME_EN : CONSUMER_GUIDE_FILENAME_KO;

  useEffect(() => {
    if (!location.hash) return undefined;
    // MarkdownView 렌더(비동기 아님)와 레이아웃 완료 이후 스크롤하도록 다음 프레임으로 지연.
    const frame = requestAnimationFrame(() => {
      const target = scrollToHashTarget(location.hash);
      // #689 §R689-1-⑦ — 검색/딥링크 진입 대상 섹션 1.8s 하이라이트(연속 이동 시 이전 타이머 clear).
      highlightCleanupRef.current?.();
      highlightCleanupRef.current = target ? highlightAnchorTarget(target) : null;
    });
    return () => {
      cancelAnimationFrame(frame);
      highlightCleanupRef.current?.();
      highlightCleanupRef.current = null;
    };
  }, [location.hash]);

  const handleDownload = useCallback(() => {
    const blob = new Blob([consumerGuide.content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = downloadFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [consumerGuide.content, downloadFilename]);

  return (
    <div>
      <PageHeader
        title={t('gettingStartedGuide.consumerGuide.title')}
        subtitle={t('gettingStartedGuide.consumerGuide.subtitle')}
        breadcrumb={
          <span>
            <Link
              to="/getting-started"
              style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}
            >
              {t('gettingStartedGuide.page.title')}
            </Link>
            {' > '}
            {t('gettingStartedGuide.consumerGuide.title')}
          </span>
        }
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
            <Link
              to="/getting-started"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--spacing-2)',
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                textDecoration: 'none',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              &larr; {t('gettingStartedGuide.consumerGuide.back')}
            </Link>
            <button
              type="button"
              onClick={handleDownload}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--spacing-2)',
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: 'var(--color-brand-primary)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('gettingStartedGuide.consumerGuide.download')}
            </button>
          </div>
        }
      />
      <div style={{ padding: 'var(--spacing-6)', maxWidth: 960, margin: '0 auto' }}>
        <MarkdownView markdownText={consumerGuide.content} contentId="consumer-guide-content" />
      </div>
    </div>
  );
}
